import {useAnalytics} from '@github-ui/use-analytics'
import useIsMounted from '@github-ui/use-is-mounted'
import {FocusKeys} from '@primer/behaviors'
import {FormControl, Spinner, Textarea, TextInput, useFocusZone, IconButton, CounterLabel} from '@primer/react'
import type {DialogProps} from '@primer/react/experimental'
import {Dialog} from '@primer/react/experimental'
import {type PropsWithChildren, useCallback, useReducer, useState} from 'react'
import {FileIcon, XIcon} from '@primer/octicons-react'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {DiscardBatchConfirmationDialog} from '../DiscardBatchConfirmationDialog'

export type SuggestionIdentifier = {
  commentDatabaseId: number
  suggestionIndex: number
}

export type SuggestionItem = {
  commentDatabaseId: number
  suggestionIndex: number
  path: string
  bodyHTML: string
}

function DialogFooter({children, footerButtons}: PropsWithChildren<Pick<DialogProps, 'footerButtons'>>) {
  const {containerRef: footerRef} = useFocusZone({
    bindKeys: FocusKeys.ArrowHorizontal | FocusKeys.Tab,
    focusInStrategy: 'closest',
  })
  return footerButtons ? (
    <Dialog.Footer ref={footerRef as React.RefObject<HTMLDivElement>}>
      <div className="d-flex flex-column">
        <div className="d-flex flex-row gap-2 flex-justify-end">
          <Dialog.Buttons buttons={footerButtons} />
        </div>
        {children}
      </div>
    </Dialog.Footer>
  ) : null
}

type FormState = {
  commitMessage: string
  extendedDescription: string
}

type FormAction = {
  type: ThisType<typeof actions.CHANGE_COMMIT_MESSAGE> | ThisType<typeof actions.CHANGE_EXTENDED_DESCRIPTION>
  payload: string
}

const actions = {
  CHANGE_COMMIT_MESSAGE: 'CHANGE_COMMIT_MESSAGE',
  CHANGE_EXTENDED_DESCRIPTION: 'CHANGE_EXTENDED_DESCRIPTION',
}

const baseErrorMessage = 'There was an error trying to commit changes'

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case actions.CHANGE_COMMIT_MESSAGE: {
      return {
        ...state,
        commitMessage: action.payload,
      }
    }
    case actions.CHANGE_EXTENDED_DESCRIPTION:
      return {...state, extendedDescription: action.payload}
    default:
      // eslint-disable-next-line no-console
      console.error('Unknown suggested change action.')
      return state
  }
}

function getCommitMessage(authorLogins: string[]) {
  // if there's only one suggestion
  if (authorLogins.length === 1) {
    return `Apply suggestion from @${authorLogins[0]}`
  }

  // if multiple suggestions that all have the same login
  if (new Set(authorLogins).size === 1) {
    return `Apply suggestions from @${authorLogins[0]}`
  }

  // multiple suggestions with different logins
  return `Apply suggestions from code review`
}

export type ApplySuggestionDialogProps = {
  authorLogins: string[]
  batchSize: number
  onClose: () => void
  onCommit: (
    commitMessage: string,
    onError: (error: Error, type?: string, friendlyMessage?: string) => void,
    onCompleted: () => void,
  ) => void
  defaultCommitMessage?: string
  dialogTitle?: string
  suggestions?: SuggestionItem[]
  onRemoveSuggestion?: (suggestionId: SuggestionIdentifier) => void
  onDiscardBatch?: () => void
}

export function ApplySuggestionDialog({
  authorLogins,
  batchSize,
  onClose,
  onCommit,
  defaultCommitMessage,
  dialogTitle,
  suggestions,
  onRemoveSuggestion,
  onDiscardBatch,
}: ApplySuggestionDialogProps) {
  const isMounted = useIsMounted()
  const {sendAnalyticsEvent} = useAnalytics()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [formValues, setFormValues] = useReducer(formReducer, {
    commitMessage: defaultCommitMessage || getCommitMessage(authorLogins),
    extendedDescription: '',
  })
  const handleError = useCallback(
    (_error: Error, _type?: string, friendlyMessage?: string) => {
      if (!isMounted()) return

      let serverErrorMessage = baseErrorMessage
      if (friendlyMessage) serverErrorMessage += `: ${friendlyMessage}`
      else serverErrorMessage += '.'

      setIsSubmitting(false)
      setErrorMessage(serverErrorMessage)
    },
    [isMounted],
  )

  const handleCompleted = useCallback(() => {
    if (!isMounted()) return
    setIsSubmitting(false)
    if (errorMessage) setErrorMessage(undefined)
  }, [errorMessage, isMounted])

  const handleOnCommitClick = useCallback(() => {
    if (isSubmitting) return

    setIsSubmitting(true)
    sendAnalyticsEvent('comments.commit_suggestion', 'COMMIT_SUGGESTION_DIALOG_BUTTON', {batchSize})
    onCommit(`${formValues.commitMessage}\n\n${formValues.extendedDescription}`, handleError, handleCompleted)
  }, [
    batchSize,
    formValues.commitMessage,
    formValues.extendedDescription,
    handleCompleted,
    handleError,
    isSubmitting,
    onCommit,
    sendAnalyticsEvent,
  ])

  const handleDiscardClick = useCallback(() => {
    setShowDiscardConfirmation(true)
  }, [])

  const handleDiscardConfirm = useCallback(() => {
    setShowDiscardConfirmation(false)
    onDiscardBatch?.()
  }, [onDiscardBatch])

  const handleDiscardCancel = useCallback(() => {
    setShowDiscardConfirmation(false)
  }, [])

  const title = batchSize > 1 ? 'Apply suggestions' : 'Apply suggestion'

  const renderFooter = () => {
    if (onDiscardBatch) {
      return (
        <Dialog.Footer>
          <div className="d-flex flex-column width-full">
            <div className="d-flex flex-row flex-justify-between width-full">
              <Dialog.Buttons
                buttons={[{content: 'Discard batch', onClick: handleDiscardClick, buttonType: 'danger'}]}
              />
              <div className="d-flex flex-row gap-2">
                <Dialog.Buttons
                  buttons={[
                    {content: 'Cancel', onClick: onClose},
                    {
                      content: (
                        <span className="d-flex flex-row flex-items-center">
                          Apply suggestions
                          {suggestions && (
                            <CounterLabel className="ml-1 color-fg-inherit">{suggestions.length}</CounterLabel>
                          )}
                          {isSubmitting && <Spinner size="small" className="ml-1" />}
                        </span>
                      ),
                      onClick: handleOnCommitClick,
                      buttonType: 'primary',
                    },
                  ]}
                />
              </div>
            </div>
            {errorMessage && (
              <FormControl.Validation variant="error" className="mt-2 pt-1 flex-justify-end">
                {errorMessage}
              </FormControl.Validation>
            )}
          </div>
        </Dialog.Footer>
      )
    }

    return (
      <DialogFooter
        footerButtons={[
          {content: 'Cancel', onClick: onClose},
          {
            content: (
              <span className="d-flex flex-row flex-items-center">
                Apply Suggestion{isSubmitting && <Spinner size="small" className="ml-1" />}
              </span>
            ),
            onClick: handleOnCommitClick,
            buttonType: 'primary',
          },
        ]}
      >
        {errorMessage && (
          <FormControl.Validation variant="error" className="mt-2 pt-1 flex-justify-end">
            {errorMessage}
          </FormControl.Validation>
        )}
      </DialogFooter>
    )
  }

  return (
    <>
      <Dialog title={dialogTitle || title} renderFooter={renderFooter} onClose={onClose}>
        <FormControl className="mb-2">
          <FormControl.Label required>Commit message</FormControl.Label>
          <TextInput
            block
            name="commit_message"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFormValues({type: actions.CHANGE_COMMIT_MESSAGE, payload: event.target.value})
            }
            value={formValues.commitMessage}
          />
        </FormControl>
        <FormControl>
          <FormControl.Label>Extended description</FormControl.Label>
          <Textarea
            block
            rows={3}
            placeholder="Add an optional description..."
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              setFormValues({type: actions.CHANGE_EXTENDED_DESCRIPTION, payload: event.target.value})
            }
            value={formValues.extendedDescription}
          />
        </FormControl>
        {suggestions && suggestions.length > 0 && (
          <div className="mb-3 mt-3">
            <p className="f6 text-bold mb-2">
              Suggestions to apply <CounterLabel>{suggestions.length}</CounterLabel>
            </p>
            <div
              style={{
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {suggestions.map(suggestion => (
                <div key={suggestion.commentDatabaseId} className="border rounded-2 mb-2">
                  <div
                    className="d-flex flex-items-center flex-justify-between p-2"
                    style={{
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--bgColor-muted, #f6f8fa)')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <FileIcon className="mr-2" size={16} />
                    <span
                      className="flex-1 text-mono f6"
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {suggestion.path}
                    </span>
                    {onRemoveSuggestion && (
                      <IconButton
                        aria-label={`Remove suggestion from ${suggestion.path}`}
                        icon={XIcon}
                        size="small"
                        variant="invisible"
                        onClick={() =>
                          onRemoveSuggestion({
                            commentDatabaseId: suggestion.commentDatabaseId,
                            suggestionIndex: suggestion.suggestionIndex,
                          })
                        }
                      />
                    )}
                  </div>
                  <div className="p-2">
                    <VerifiedHTMLBox
                      className="markdown-body"
                      comment-testid={`Comment body html for comment`}
                      html={suggestion.bodyHTML as SafeHTMLString}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Dialog>
      <DiscardBatchConfirmationDialog
        isOpen={showDiscardConfirmation}
        onConfirm={handleDiscardConfirm}
        onCancel={handleDiscardCancel}
        suggestionsCount={suggestions?.length || batchSize}
      />
    </>
  )
}

try{ DialogFooter.displayName ||= 'DialogFooter' } catch {}
try{ ApplySuggestionDialog.displayName ||= 'ApplySuggestionDialog' } catch {}